import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { setOtherUsersCalendarData } from '../Redux/calendarSlice';
import { transformIpCalendarData } from '../data/transformers/calendarTransform';
import { saveOtherUserCalendarDataToCache, loadOtherUserCalendarDataFromCache } from '../data/calendarCache';

const TAG = '[useCalendarDataOther]';

/**
 * Hook to load another user's calendar data via RPC with 3-tier caching.
 *
 * Three-tier cache strategy (checked in order):
 *   1. Redux — if the requested date range is already in otherUsersCalendarData[userId], use it (no network call)
 *   2. localStorage — if Redux doesn't cover the range, check disk cache.
 *      If covered, populate Redux from cache + do a background Supabase reload.
 *   3. Supabase — if neither cache covers the range, do a blocking RPC call,
 *      write results to Redux and localStorage.
 *
 * @param {string} userId - The target user's ID to fetch calendar data for
 * @param {string} startMonth - Start month in YYYY-MM format (e.g. '2026-02')
 * @param {string} endMonth - End month in YYYY-MM format (e.g. '2026-08')
 * @returns {{ calendarData: object, loading: boolean, refresh: function }}
 */
export default function useCalendarDataOther(userId, startMonth = null, endMonth = null) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState({});

  // Redux state for other users' calendar data
  const reduxOtherUsers = useSelector((s) => s.calendar.otherUsersCalendarData);
  const reduxCache = reduxOtherUsers?.[userId];

  // ── Date range calculation ───────────────────────────────────────────
  const calculateDateRange = useCallback(() => {
    let rangeStart, rangeEnd;
    if (startMonth && endMonth) {
      rangeStart = `${startMonth}-01`;
      rangeEnd = `${endMonth}-01`;
    } else {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const next = new Date(now.getFullYear(), now.getMonth() + 3, 1);
      rangeStart = prev.toISOString().split('T')[0];
      rangeEnd = next.toISOString().split('T')[0];
    }
    return { rangeStart, rangeEnd };
  }, [startMonth, endMonth]);

  // ── Range coverage check ─────────────────────────────────────────────
  // Pure function — returns true if the requested range is fully within the cached range.
  const isRangeCovered = useCallback((requestedStart, requestedEnd, cachedStart, cachedEnd) => {
    if (!cachedStart || !cachedEnd) return false;
    return requestedStart >= cachedStart && requestedEnd <= cachedEnd;
  }, []);

  // ── RPC data transformer ─────────────────────────────────────────────
  // Converts the raw Supabase RPC response into the date-keyed calendar
  // data structure using the shared transform function (which includes
  // timezone reassignment).
  const transformRpcData = useCallback((rpcData) => {
    return transformIpCalendarData(rpcData);
  }, []);

  // ── Load from Supabase ─────────────────────────────────────────────────
  // Calls the RPC, transforms the response, writes to Redux + localStorage.
  // If isBackground=true, doesn't touch loading state (used when localStorage
  // cache was used and we want to refresh in the background).
  const loadFromSupabase = useCallback(async (rangeStart, rangeEnd, isBackground = false) => {
    if (!userId) {
      return;
    }


    if (!isBackground) {
      setLoading(true);
    }

    try {
      const { data, error } = await supabase.rpc('get_ip_calendar_j', {
        p_user_id: userId,
        p_range_start: rangeStart,
        p_range_end: rangeEnd,
      });

      if (error) {
        return;
      }

      const transformedData = transformRpcData(data);

      // Update Redux
      dispatch(setOtherUsersCalendarData({
        userId,
        data: transformedData,
        range_start: rangeStart,
        range_end: rangeEnd,
      }));

      // Update localStorage
      saveOtherUserCalendarDataToCache(userId, {
        data: transformedData,
        range_start: rangeStart,
        range_end: rangeEnd,
        last_updated: Date.now(),
      });
    } catch (err) {
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [userId, transformRpcData, dispatch]);

  // ── Main load function (three-tier cache) ────────────────────────────
  // Checks Redux → localStorage → Supabase in order.
  const loadCalendarData = useCallback(async () => {
    if (!userId) return;

    const { rangeStart, rangeEnd } = calculateDateRange();

    // 1. Check Redux
    const reduxCovered = isRangeCovered(rangeStart, rangeEnd, reduxCache?.range_start, reduxCache?.range_end);

    if (reduxCovered) {
      setCalendarData(reduxCache.data);
      setLoading(false);
      return;
    }

    // 2. Check localStorage
    const cachedData = loadOtherUserCalendarDataFromCache(userId);

    if (cachedData) {
      const localCovered = isRangeCovered(rangeStart, rangeEnd, cachedData.range_start, cachedData.range_end);

      if (localCovered) {
        dispatch(setOtherUsersCalendarData({
          userId,
          data: cachedData.data,
          range_start: cachedData.range_start,
          range_end: cachedData.range_end,
        }));
        setCalendarData(cachedData.data);
        setLoading(false);

        // Background reload from Supabase (non-blocking)
        loadFromSupabase(rangeStart, rangeEnd, true);
        return;
      }
    }

    // 3. Load from Supabase (blocking)
    await loadFromSupabase(rangeStart, rangeEnd, false);
  }, [userId, reduxCache, calculateDateRange, isRangeCovered, dispatch, loadFromSupabase]);

  // ── Initial load ─────────────────────────────────────────────────────
  // Load when userId or date range changes
  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // ── Sync: Redux → local state ────────────────────────────────────────
  // Keeps local calendarData in sync with Redux so consumers can read it
  // from the hook's return value.
  useEffect(() => {
    if (reduxCache?.data && Object.keys(reduxCache.data).length > 0) {
      setCalendarData(reduxCache.data);
    }
  }, [reduxCache]);

  // ── Manual refresh ───────────────────────────────────────────────────
  const refresh = useCallback(() => {
    const { rangeStart, rangeEnd } = calculateDateRange();
    loadFromSupabase(rangeStart, rangeEnd, false);
  }, [calculateDateRange, loadFromSupabase]);

  return { calendarData, loading, refresh };
}
